import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/
import { useContext, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Menu, MenuItem, Skeleton, Slider, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import AvatarEditor from 'react-avatar-editor';
import { MainContext } from '../../context';
import { useNamespaceDetails, useUpdateNamespaceDetails, useUpdateNamespaceLogo } from './use-namespace-details';
import { EmptyPlaceholder } from '../../pages/user/settings/settings-primitives';
// Square empty state standing in for a missing logo; same footprint as the image
// so the Edit button keeps its place.
const LogoPlaceholder = styled(EmptyPlaceholder)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    aspectRatio: '1 / 1',
    padding: '1rem'
});
/**
 * Namespace logo sidebar: shows the logo (or a "no logo" placeholder) and, for
 * owners, a GitHub-style Edit menu whose upload/removal apply immediately,
 * independent of the details form.
 */
export const NamespaceLogo = props => {
    const context = useContext(MainContext);
    const editor = useRef(null);
    const fileInput = useRef(null);
    const detailsQuery = useNamespaceDetails(props.namespace.name);
    const details = detailsQuery.data;
    const uploadLogo = useUpdateNamespaceLogo();
    const updateDetails = useUpdateNamespaceDetails();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [logoFile, setLogoFile] = useState();
    const [editing, setEditing] = useState(false);
    const [editorScale, setEditorScale] = useState(1);
    const [editorRotation, setEditorRotation] = useState(0);
    const [editorPosition, setEditorPosition] = useState();
    const canEdit = Boolean(props.namespace.detailsUrl);
    const handleFileChosen = (event) => {
        const file = event.target.files?.[0];
        // allow re-selecting the same file later
        event.target.value = '';
        if (!file) {
            return;
        }
        if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
            context.handleError(new Error(`Unsupported file type '${file.type}'`));
            return;
        }
        setLogoFile(file);
        setEditorScale(1);
        setEditorRotation(0);
        setEditorPosition(undefined);
        setEditing(true);
    };
    const handleApplyLogo = () => {
        const canvasScaled = editor.current?.getImageScaledToCanvas();
        canvasScaled?.toBlob(async (blob) => {
            if (!blob || !logoFile) {
                return;
            }
            try {
                await uploadLogo.mutateAsync({
                    namespaceName: props.namespace.name,
                    detailsUrl: props.namespace.detailsUrl,
                    file: blob,
                    fileName: logoFile.name
                });
            }
            catch (err) {
                context.handleError(err);
            }
        });
        setEditing(false);
    };
    const removeLogo = async () => {
        setMenuAnchor(null);
        if (!details) {
            return;
        }
        try {
            // The update endpoint deletes the stored logo when the payload's logo is
            // empty; send only the editable fields, unchanged. Blank social links must
            // be dropped — the server validates any non-null value as a full URL.
            await updateDetails.mutateAsync({
                detailsUrl: props.namespace.detailsUrl,
                details: {
                    name: details.name,
                    displayName: details.displayName,
                    description: details.description,
                    website: details.website,
                    supportLink: details.supportLink,
                    socialLinks: {
                        linkedin: details.socialLinks.linkedin || undefined,
                        github: details.socialLinks.github || undefined,
                        twitter: details.socialLinks.twitter || undefined
                    },
                    logo: undefined
                }
            });
        }
        catch (err) {
            context.handleError(err);
        }
    };
    const adjustScale = (x) => (x < 1 ? 0.5 + x / 2 : x);
    const percentageLabelFormat = (value) => `${Math.round(value * 100)}%`;
    const handleEditorScaleChange = (_event, value) => setEditorScale(typeof value === 'number' ? value : value[0]);
    const renderLogo = () => {
        if (detailsQuery.isLoading) {
            return _jsx(Skeleton, { variant: 'rounded', sx: { width: '100%', height: 'auto', aspectRatio: '1 / 1' } });
        }
        if (!details?.logo) {
            return _jsx(LogoPlaceholder, { children: "No logo" });
        }
        return (_jsx(Box, { component: 'img', src: details.logo, alt: `${details.displayName || props.namespace.name} logo`, sx: {
                display: 'block',
                width: '100%',
                aspectRatio: '1 / 1',
                objectFit: 'contain',
                borderRadius: theme => `${theme.shape.borderRadiusCard}px`,
                border: '1px solid',
                borderColor: 'divider'
            } }));
    };
    return (_jsxs(Box, { sx: { position: 'relative' }, children: [renderLogo(), canEdit ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', size: 'small', startIcon: _jsx(EditIcon, { sx: { fontSize: '0.8125rem' } }), onClick: event => setMenuAnchor(event.currentTarget), sx: {
                            position: 'absolute',
                            bottom: '0.5rem',
                            left: '0.5rem',
                            bgcolor: 'background.paper',
                            boxShadow: 'var(--shadow)',
                            '&:hover': { bgcolor: 'background.paper' }
                        }, children: "Edit" }), _jsxs(Menu, { open: Boolean(menuAnchor), anchorEl: menuAnchor, onClose: () => setMenuAnchor(null), children: [_jsx(MenuItem, { onClick: () => {
                                    setMenuAnchor(null);
                                    fileInput.current?.click();
                                }, children: "Upload a logo\u2026" }), details?.logo ? (_jsx(MenuItem, { onClick: removeLogo, sx: { color: 'error.main' }, children: "Remove logo" })) : null] }), _jsx(Box, { component: 'input', ref: fileInput, type: 'file', accept: 'image/png,image/jpeg', onChange: handleFileChosen, sx: { display: 'none' } }), _jsxs(Dialog, { open: editing, onClose: () => setEditing(false), children: [_jsx(DialogTitle, { children: "Edit namespace logo" }), _jsx(DialogContent, { sx: { overflowY: 'unset' }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, sx: { display: 'flex' }, children: _jsx(AvatarEditor, { style: { margin: '0 auto' }, ref: editor, image: logoFile ?? '', width: 120, height: 120, border: 8, color: [200, 200, 200, 0.6], scale: adjustScale(editorScale), rotate: editorRotation, position: editorPosition, onPositionChange: setEditorPosition }) }), _jsx(Grid, { item: true, xs: 12, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, children: _jsx(ZoomOutIcon, {}) }), _jsx(Grid, { item: true, xs: true, children: _jsx(Slider, { min: 0, max: 2, step: 0.01, scale: adjustScale, color: 'secondary', valueLabelDisplay: 'auto', valueLabelFormat: percentageLabelFormat, value: editorScale, onChange: handleEditorScaleChange }) }), _jsx(Grid, { item: true, children: _jsx(ZoomInIcon, {}) })] }) }), _jsx(Grid, { item: true, xs: 12, sx: { display: 'flex' }, children: _jsxs(Stack, { direction: 'row', spacing: 2, sx: { margin: '0 auto' }, children: [_jsx(IconButton, { onClick: () => setEditorRotation(editorRotation - 90), title: 'Rotate image counter-clockwise', children: _jsx(RotateLeftIcon, {}) }), _jsx(IconButton, { onClick: () => setEditorRotation(editorRotation + 90), title: 'Rotate image clockwise', children: _jsx(RotateRightIcon, {}) })] }) })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setEditing(false), children: "Cancel" }), _jsx(Button, { variant: 'contained', color: 'secondary', autoFocus: true, onClick: handleApplyLogo, children: "Apply logo" })] })] })] })) : null] }));
};
//# sourceMappingURL=namespace-logo.js.map